import React from "react";
import GridLayout, { WidthProvider } from "react-grid-layout";
import DashboardRefreshButton from "./DashboardRefreshButton";
import { TOUR_STEP_IDS } from "@/lib/tour-constants";

const ResponsiveGridLayout = WidthProvider(GridLayout);
import { Card } from "@/components/ui/card";
import dashboardConfig, { PA_dashboardConfig } from "./dashboardConfig";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import type { DashboardLayout, DashboardLayoutItem } from "@/services/userService";

interface User {
  dashboardConfig?: DashboardLayout;
  mobileDashboardConfig?: DashboardLayout;
}

const SELF_SERVICE_WIDGET_LAYOUT: DashboardLayoutItem[] = [
  {
    i: "overall-progress",
    x: 0,
    y: 0,
    w: 12,
    h: 2,
    idx: 0,
  },
  {
    i: "step1-tracking",
    x: 0,
    y: 2,
    w: 6,
    h: 9,
    idx: 1,
  },
];

const SELF_SERVICE_WIDGET_COMPONENTS: Record<string, React.ComponentType<any>> = {
  "overall-progress": OverallProgressCard,
  "step1-tracking": StepTrackingCard,
};

const SELF_SERVICE_WIDGET_IDS = new Set(Object.keys(SELF_SERVICE_WIDGET_COMPONENTS));

// Function to get the appropriate dashboard config based on plan
const getDashboardConfig = (plan?: string) => {
  // Use PA_dashboardConfig for 'Starter' plan, otherwise use default dashboardConfig
  return plan === 'Starter' || plan === 'Starter Plan' || plan === 'starter' || plan === 'starter plan' ? PA_dashboardConfig : dashboardConfig;
};

// Generate a default layout based on the selected dashboard config
const generateDefaultLayout = (config: typeof dashboardConfig): DashboardLayout =>
  config.map((item, idx) => {
    const anyItem = item as any;
    const w = typeof anyItem.w === 'number' ? anyItem.w : 3;
    const h = typeof anyItem.h === 'number' ? anyItem.h : 3;
    const x = typeof anyItem.x === 'number' ? anyItem.x : (idx % 4) * 3; // fallback to grid pattern
    const y = typeof anyItem.y === 'number' ? anyItem.y : Math.floor(idx / 4) * 3;
    return {
      i: item.id,
      x,
      y,
      w,
      h,
      idx,
    };
  });

const convertToMobileLayout = (layout: DashboardLayout): DashboardLayout => {
  let currentY = 0;
  return layout
    .slice()
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map((item) => {
      const height = item.h ?? 1;
      const adjusted = {
        ...item,
        x: 0,
        w: 12,
        y: currentY,
      };
      currentY += height;
      return adjusted;
    });
};

const mergeWithSelfService = (baseLayout: DashboardLayout, includeSelfService: boolean): DashboardLayout => {
  if (!includeSelfService) return baseLayout;
  const existingIds = new Set(baseLayout.map((item) => item.i));
  const extras = SELF_SERVICE_WIDGET_LAYOUT.filter((item) => !existingIds.has(item.i));
  return [...extras, ...baseLayout];
};

import { useEffect, useState, useCallback, useMemo } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getUserInfo, saveUserInfo } from "@/services/userService";
import { toast } from "sonner";
import { useTenancy } from "@/contexts/TenancyContext";
import { isAuthLoaded } from "@/lib/authState";
import { auth } from "@/lib/firebase";
import OverallProgressCard from "@/app/components/onboarding/OverallProgressCard";
import StepTrackingCard from "@/app/components/onboarding/StepTrackingCard";
import { getAvailableFeatures } from "@/utils/featuresUtil";
import { DashboardWidgetProvider, useDashboardWidgetContext } from "@/contexts/DashboardWidgetContext";
import { deiterateAPI } from "@/js/external-js/variableModules/utils/deiterateAPI.util";
import { useNavigate } from "react-router-dom";

// Inner component that uses the widget context
const DashboardGridInner: React.FC = () => {
  const availableFeatures = getAvailableFeatures();
  const isSelfServiceEnabled = !!availableFeatures.find(f => f.name === 'Self-Service');
  console.log('IS SELF SERVICE ENABLED: ', isSelfServiceEnabled);
  const { activeTenancy } = useTenancy();
  const navigate = useNavigate();
  const { autoRefreshTriggered, setAutoRefreshTriggered, resetStatuses } = useDashboardWidgetContext();
  const [dashboardConfig, setDashboardConfig] = useState(() => getDashboardConfig(activeTenancy?.plan));
  const [layout, setLayout] = useState<DashboardLayout>(() => generateDefaultLayout(getDashboardConfig(activeTenancy?.plan)));
  const [mobileLayout, setMobileLayout] = useState<DashboardLayout>(() =>
    convertToMobileLayout(generateDefaultLayout(getDashboardConfig(activeTenancy?.plan)))
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const justLoadedRef = React.useRef(false);
  const autoRefreshAttemptedRef = React.useRef(false);
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Handle auto-refresh when most widgets have errors
  useEffect(() => {
    if (autoRefreshTriggered && !autoRefreshAttemptedRef.current && activeTenancy?.customerID) {
      autoRefreshAttemptedRef.current = true;
      setIsAutoRefreshing(true);
      
      console.log('[DashboardGrid] Auto-triggering dashboard refresh due to widget errors...');
      toast.info('Dashboard data appears stale. Refreshing automatically...');
      
      deiterateAPI.POST("/dashboard/refresh", { customerIds: [activeTenancy.customerID] })
        .then(() => {
          toast.success('Dashboard refresh triggered. Reloading...');
          // Reset statuses and reload after a short delay
          setTimeout(() => {
            resetStatuses();
            setAutoRefreshTriggered(false);
            navigate("/dashboard", { replace: true });
          }, 2000);
        })
        .catch((error: any) => {
          console.error('[DashboardGrid] Auto-refresh failed:', error);
          toast.error('Auto-refresh failed. Please try manually refreshing.');
          setIsAutoRefreshing(false);
          setAutoRefreshTriggered(false);
        });
    }
  }, [autoRefreshTriggered, activeTenancy?.customerID, navigate, resetStatuses, setAutoRefreshTriggered]);

  // Merge loaded layout with default to ensure all widgets are present and sized correctly
  const mergeLayoutWithDefaults = (loadedLayout: DashboardLayout, config: typeof dashboardConfig): DashboardLayout => {
    const layoutMap = new Map(
      loadedLayout
        .filter(item => !SELF_SERVICE_WIDGET_IDS.has(item.i))
        .map(item => [item.i, item])
    );
    const defaultLayout = generateDefaultLayout(config);
    return config.map((item, idx) => {
      const saved = layoutMap.get(item.id);
      return saved ? { ...saved, idx } : { ...defaultLayout[idx] };
    });
  };

  // Update dashboard config when plan changes
  useEffect(() => {
    const newConfig = getDashboardConfig(activeTenancy?.plan);
    setDashboardConfig(newConfig);
    // Reset to default layout when plan changes
    const defaultLayout = generateDefaultLayout(newConfig);
    setLayout(defaultLayout);
    setMobileLayout(convertToMobileLayout(defaultLayout));
  }, [activeTenancy?.plan]);

  // Load dashboard layout from user profile
  // const loadDashboardLayout = useCallback(async () => {
  //   try {
  //     const user = await getUserInfo() as unknown as User;
  //     if (user?.dashboardConfig && Array.isArray(user.dashboardConfig)) {
  //       console.log('[DashboardGrid] Loaded layout:', user.dashboardConfig);
  //       setLayout(mergeLayoutWithDefaults(user.dashboardConfig, dashboardConfig));
  //       justLoadedRef.current = true;
  //     }
  //   } catch (e) {
  //     console.error('Failed to load dashboard layout', e);
  //     // Ignore errors, fallback to default
  //   } finally {
  //     setIsLoading(false);
  //   }
  // }, [dashboardConfig]);
  const loadDashboardLayout = useCallback(async () => {
    try {
      console.log('[DashboardGrid] Loading dashboard layout...');
      
      // Double-check auth state before proceeding
      if (!isAuthLoaded()) {
        console.log('[DashboardGrid] Auth not ready during load, skipping...');
        return;
      }
      
      const user = await getUserInfo() as unknown as User;
      if (!user) {
        console.log('[DashboardGrid] No user data available, using default layout');
        // For fresh tenancies, user document might not exist yet
        const defaultLayout = generateDefaultLayout(dashboardConfig);
        setLayout(defaultLayout);
        setMobileLayout(convertToMobileLayout(defaultLayout));
        justLoadedRef.current = true;
        setIsLoading(false);
        return;
      }
      
      if (user?.dashboardConfig && Array.isArray(user.dashboardConfig) && user.dashboardConfig.length > 0) {
        console.log('[DashboardGrid] Loaded layout:', user.dashboardConfig);
        setLayout(mergeLayoutWithDefaults(user.dashboardConfig, dashboardConfig));
      } else {
        // Use the default dashboardConfig layout directly
        console.log('[DashboardGrid] Using default layout');
        const defaultLayout = generateDefaultLayout(dashboardConfig);
        setLayout(defaultLayout);
      }

      if (user?.mobileDashboardConfig && Array.isArray(user.mobileDashboardConfig) && user.mobileDashboardConfig.length > 0) {
        console.log('[DashboardGrid] Loaded mobile layout:', user.mobileDashboardConfig);
        const mergedMobile = mergeLayoutWithDefaults(user.mobileDashboardConfig, dashboardConfig);
        setMobileLayout(convertToMobileLayout(mergedMobile));
      } else {
        const defaultMobileLayout = convertToMobileLayout(generateDefaultLayout(dashboardConfig));
        setMobileLayout(defaultMobileLayout);
      }

      justLoadedRef.current = true;
    } catch (e) {
      console.error('Failed to load dashboard layout', e);
      // Fallback to default layout
      const fallbackLayout = generateDefaultLayout(dashboardConfig);
      setLayout(fallbackLayout);
      setMobileLayout(convertToMobileLayout(fallbackLayout));
      justLoadedRef.current = true;
    } finally {
      setIsLoading(false);
    }
  }, [dashboardConfig]);

  useEffect(() => {
    if (!activeTenancy || !activeTenancy.id || ["", "Loading..."]?.includes(activeTenancy.id)) return;
    
    // Add additional check to ensure auth is ready and user document exists
    if (!isAuthLoaded()) {
      console.log('[DashboardGrid] Auth not ready, waiting...');
      return;
    }
    
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.log('[DashboardGrid] No current user, waiting...');
      return;
    }
    
    loadDashboardLayout();

  }, [loadDashboardLayout, activeTenancy]);

  const combinedDesktopLayout = useMemo(() => {
    return mergeWithSelfService(layout, isSelfServiceEnabled);
  }, [isSelfServiceEnabled, layout]);

  const combinedMobileLayout = useMemo(() => {
    const merged = mergeWithSelfService(mobileLayout, isSelfServiceEnabled);
    return convertToMobileLayout(merged);
  }, [isSelfServiceEnabled, mobileLayout]);

  // Save layout to user profile when changed (robust, with debug)
  const handleLayoutChange = (newLayout: DashboardLayout) => {
    if (justLoadedRef.current) {
      justLoadedRef.current = false;
      return; // Skip save on initial load
    }
    const layoutCopy = newLayout
      .filter(item => !SELF_SERVICE_WIDGET_IDS.has(item.i))
      .map(item => ({ ...item }));
    if (isMobile) {
      const mobileSanitized = convertToMobileLayout(layoutCopy);
      setMobileLayout(mobileSanitized);
      console.log('[DashboardGrid] Saving mobile layout:', mobileSanitized);
      saveUserInfo({ mobileDashboardConfig: mobileSanitized })
        .then(() => toast.success('Mobile dashboard layout saved'))
        .catch(e => {
          console.error('Failed to save mobile dashboard layout', e);
          toast.error('Failed to save mobile dashboard layout');
        });
      return;
    }
    setLayout(layoutCopy);
    console.log('[DashboardGrid] Saving layout:', layoutCopy);
    saveUserInfo({ dashboardConfig: layoutCopy })
      .then(() => toast.success('Dashboard layout saved'))
      .catch(e => {
        console.error('Failed to save dashboard layout', e);
        toast.error('Failed to save dashboard layout');
      });
  };

  if (isLoading || !activeTenancy || !isAuthLoaded()) {
    return <div className="w-full h-[400px] flex items-center justify-center"><span>Loading dashboard...</span></div>;
  }

  // Show auto-refresh in progress state
  if (isAutoRefreshing) {
    return (
      <div className="w-full h-[400px] flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <span className="text-muted-foreground">Refreshing dashboard data...</span>
      </div>
    );
  }

  const layoutToRender = isMobile ? combinedMobileLayout : combinedDesktopLayout;

  return (
    <div
      id={TOUR_STEP_IDS.DASHBOARD_TITLE}
      className="w-full overflow-y-auto max-h-screen sm:max-h-none sm:overflow-visible"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      <ResponsiveGridLayout
        className="layout"
        layout={layoutToRender}
        cols={12}
        rowHeight={100}
        onLayoutChange={handleLayoutChange}
        onDragStop={handleLayoutChange}
        onResizeStop={handleLayoutChange}
        draggableCancel=".cancelDrag"
        isDraggable={!isMobile}
        isResizable={!isMobile}
        compactType={isMobile ? undefined : 'vertical'}
      >
        {layoutToRender.map((item) => {
          const widget = dashboardConfig.find((w) => w.id === item.i);
          if (!widget) return null;
          const WidgetComponent = widget.component as React.ComponentType<any>;
          return (
            <Card key={item.i} className="w-full h-full overflow-hidden" data-grid={item}>
              <WidgetComponent {...widget.props} />
            </Card>
          );
        })}
      </ResponsiveGridLayout>
    </div>
  );
};

// Wrapper component that provides the widget context
const DashboardGrid: React.FC = () => {
  return (
    <DashboardWidgetProvider>
      <DashboardGridInner />
    </DashboardWidgetProvider>
  );
};

export default DashboardGrid;
